///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtthEntityInfoWindowContent } from '../gtth-entity-info-window-content-interface';
import {
    EngineProductDTO,
    GtthKeyValueDTO,
    GtthStepGroupEnum,
    NodeTypeEnum,
    SectorPolarityEnum
} from '../../../../generated/shared-types.model';
import { GtthModifyUnitInfoCommand } from '../../engine/commands/node/modify-unit-info-command';
import { NodeFeature } from '../../entities/node/node-feature';
import { GtthHistoryService } from '../../engine/history-service';
import { GtthRemoveTerminalCommand } from '../../engine/commands/node/remove-terminal-command';
import { GtthCreateLosesToStationNeighborsCommand } from '../../engine/commands/link/create-loses-to-station-neighbors-command';
import { GtthVerifyLosesToStationNeighborsCommand } from '../../engine/commands/link/verify-loses-to-Station-neighbors-command';
import { GtthDisableMultipleEntitiesCommand } from '../../engine/commands/disable-multiple-entities-command';
import { GtthEnableMultipleEntitiesCommand } from '../../engine/commands/enable-multiple-entities-command';
import { GtthNodeFeatureDtoModel } from '../../../../services/project-socket/dto/gtth-node-feature-dto.model';
import { GtthEventManager } from '../../../../services/event-manager.service';
import { GtthLayerDtoModel } from '../../../../services/project-socket/dto/gtth-layer-dto.model';
import { GtthInfoWindowsService } from '../info-windows-service';
import { ProjectProductsService } from '../../engine/project-products-service';
import {
    SuiEditableSelectOption,
    SuiSelectControlComponent,
    SuiSelectEnumControlComponent,
    SuiTypeEditableSelect,
    SuiTypeEnum,
    SuiTypeNumber,
    SuiTypeSelect
} from '@siklu/sui-lib/infrastructure';
import { IsPopEnum } from './is-pop.enum';
import { GtthProjectStateService } from '../../engine/project-state.service';
import { SuiProjectEditableSelectComponent } from '../../../../shared/sui-lib-customisations/sui-select/editable/project/sui-project-editable-select.component';
import { SuiTranslateEnumPipe, SuiTranslatePipe } from '@siklu/sui-lib';

@Component({
    selector: 'winde-unit-info',
    templateUrl: './unit-info.component.html',
    styleUrls: ['../entities-info-window.scss'],
    imports: [
        SuiTranslateEnumPipe,
        SuiTranslatePipe,
        SuiSelectControlComponent,
        SuiSelectEnumControlComponent,
        SuiProjectEditableSelectComponent
    ]
})
export class GtthUnitInfoComponent extends GtthEntityInfoWindowContent implements OnInit, OnDestroy {
    public static readonly CUSTOM_ELEMENT_TAG_NAME = 'gtth-unit-info-custom-tag-element';

    public static readonly PREDEFINED_PRODUCT_AUTO_OPTION = 'Auto';

    @Output()
    public closeCallback = new EventEmitter<void>();

    public isEditingEnabled = true;

    public stepGroupEnum = GtthStepGroupEnum;

    public station: NodeFeature;
    public modifyInfoCommand: GtthModifyUnitInfoCommand;
    public allowLosStations: number[];
    public losToVerify: number[];
    public unitSectorsCount: number;
    public hasFreezeLinks: boolean;
    public isShowSectors: boolean;

    public projectHasBuProducts = true;

    public isPoPControl = new SuiTypeEnum<IsPopEnum>(undefined, [IsPopEnum.NO_POP, IsPopEnum.IS_POP], 'IsPopEnum');
    public layersControl: SuiTypeSelect<GtthKeyValueDTO>;

    public mhTypeControl = new SuiTypeEnum<NodeTypeEnum>(undefined, [NodeTypeEnum.BEST_EFFORT], 'NodeTypeEnum');
    public mhTypeEnum = NodeTypeEnum;

    public productNotDefinedOption = {
        id: GtthNodeFeatureDtoModel.PREDEFINED_PRODUCT_NOT_DEFINED,
        name: GtthUnitInfoComponent.PREDEFINED_PRODUCT_AUTO_OPTION
    } as EngineProductDTO;

    public mhTgProductControl: SuiTypeSelect<EngineProductDTO>;
    public blockedByPoleControl: SuiTypeEditableSelect<number>;
    public deviceAzimuthControl: SuiTypeEditableSelect<number>;
    public polarityControl = new SuiTypeEnum<SectorPolarityEnum>(
        undefined,
        [SectorPolarityEnum.UNSPECIFIED, SectorPolarityEnum.ODD, SectorPolarityEnum.EVEN],
        'SectorPolarityEnum'
    );

    private isUnitDelete = false;

    constructor(
        private readonly eventManager: GtthEventManager,
        public readonly historyService: GtthHistoryService,
        infoWindowService: GtthInfoWindowsService,
        private readonly projectProductsService: ProjectProductsService,
        public readonly projectStateService: GtthProjectStateService
    ) {
        super();
        infoWindowService.registerActiveInfoWindow(this);

        this.isPoPControl.isAlignedStyles = true;
        this.isPoPControl.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.mhTypeControl.isAlignedStyles = true;
        this.mhTypeControl.dropdownConfig.panelClass = 'entities-info-select-dropdown';
        this.polarityControl.isAlignedStyles = true;
        this.polarityControl.dropdownConfig.panelClass = 'entities-info-select-dropdown';
    }

    ngOnInit() {
        this.station = this.historyService.logicalTopology.getStation(this.entityId);
        this.createCommand();
    }

    setIsPoP(newIsPoPValue: IsPopEnum) {
        const isPoP = newIsPoPValue === IsPopEnum.IS_POP;
        this.modifyInfoCommand.setNewIsPopValue(isPoP);

        const mhTypeShouldBeChanged = isPoP && this.station.getMhType() !== NodeTypeEnum.BU_MUST;
        this.initOrUpdateMhTypeAndProductControls();

        if (mhTypeShouldBeChanged) {
            if (this.mhTypeControl.options.includes(NodeTypeEnum.BU_MUST)) {
                this.setMhType(NodeTypeEnum.BU_MUST);
            }
        }
    }

    setLayer(layer: GtthKeyValueDTO) {
        this.modifyInfoCommand.setNewLayerIndex(layer.key);

        this.initOrUpdateMhTypeAndProductControls();
        this.initOrUpdateBlockedByPoleControl();
        this.initOrUpdateDeviceAzimuthControl();
    }

    public setMhType(newMhType: NodeTypeEnum): void {
        this.mhTypeControl.setValue(newMhType);
        this.modifyInfoCommand.setNewMhType(newMhType);

        if (newMhType === NodeTypeEnum.BU_MUST) {
            this.setPredefinedBuProductIdOrSelectFirst();
        } else {
            this.setBuDeviceAzimuth(GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH);
            this.setPredefinedBuProductId(this.productNotDefinedOption);
        }
    }

    public setSectorPolarity(newPolarity: SectorPolarityEnum) {
        this.polarityControl?.setValue(newPolarity);
        this.modifyInfoCommand.setBuDevicePolarity(newPolarity);
    }

    public setBlockedByPoleAngle(blockedByPoleValue: number): void {
        this.blockedByPoleControl?.setValue(blockedByPoleValue);
        this.modifyInfoCommand.setBlockedByPoleAngle(blockedByPoleValue);
    }

    public setBuDeviceAzimuth(deviceAzimuth: number): void {
        this.deviceAzimuthControl?.setValue(deviceAzimuth);
        this.modifyInfoCommand.setBuDeviceAzimuth(deviceAzimuth);
    }

    public setPredefinedBuProductId(newProduct: EngineProductDTO): void {
        if (this.mhTypeControl.getValue() === NodeTypeEnum.BU_MUST) {
            this.mhTgProductControl.setValue(newProduct);
        } else {
            this.mhTgProductControl?.setValue(this.productNotDefinedOption);
        }

        this.modifyInfoCommand.setPredefinedBuProductId(newProduct.id);
    }

    ngOnDestroy() {
        this.eventManager.broadcast({ name: 'unitConfigHide', content: 'OK' });
        if (this.isValid() && !this.isUnitDelete) {
            this.historyService.doCommand(this.modifyInfoCommand);
        }
    }

    async createLos() {
        if (!this.modifyInfoCommand.isValid()) {
            return;
        }

        await this.historyService.doCommand(this.modifyInfoCommand);
        this.createCommand();

        const createLosesCommand = this.historyService.command(GtthCreateLosesToStationNeighborsCommand).create(this.entityId);
        const resolve = await this.historyService.doCommand(createLosesCommand);
        if (resolve) {
            this.allowLosStations = this.historyService.logicalTopology.getAllowStationsForLoses(this.entityId);
            this.losToVerify = this.historyService.logicalTopology.getExistLosForStation(this.entityId);
        }
    }

    async verifyLos() {
        if (!this.modifyInfoCommand.isValid()) {
            return;
        }

        await this.historyService.doCommand(this.modifyInfoCommand);
        this.createCommand();

        const createLosesCommand = this.historyService.command(GtthVerifyLosesToStationNeighborsCommand).create(this.entityId);
        const resolve = await this.historyService.doCommand(createLosesCommand);
        if (resolve) {
            this.losToVerify = this.historyService.logicalTopology.getExistLosForStation(this.entityId);
        }
    }

    async delUnit() {
        if (!this.modifyInfoCommand.isValid()) {
            return;
        }

        await this.historyService.doCommand(this.modifyInfoCommand);
        this.isUnitDelete = true;
        const deleteCommand = this.historyService.command(GtthRemoveTerminalCommand).createFromId(this.entityId);
        await this.historyService.doCommand(deleteCommand);
        this.closeCallback.next();
    }

    showConfig() {
        this.eventManager.broadcast({
            name: 'unitConfigShow',
            entityId: this.station.getId(),
            modifyCommand: this.modifyInfoCommand
        });
    }

    async toggleDisable() {
        if (!this.modifyInfoCommand.isValid()) {
            return;
        }

        await this.historyService.doCommand(this.modifyInfoCommand);

        let toggleEnableNodeCommand;
        if (this.station.isEnabled()) {
            toggleEnableNodeCommand = this.historyService.command(GtthDisableMultipleEntitiesCommand);
        } else {
            toggleEnableNodeCommand = this.historyService.command(GtthEnableMultipleEntitiesCommand);
        }
        toggleEnableNodeCommand.createFromNodeId(this.entityId);
        await this.historyService.doCommand(toggleEnableNodeCommand);
        this.createCommand();
    }

    isValid(): boolean {
        return this.modifyInfoCommand?.isValid() && this.deviceAzimuthControl.validateValue() && this.blockedByPoleControl.validateValue();
    }

    public showSectors() {
        const stationName = this.modifyInfoCommand.getName();
        if (this.isShowSectors) {
            this.historyService.logicalTopology.hideNodeSectors(stationName);
        } else {
            this.historyService.logicalTopology.showNodeSectors(stationName);
        }
        this.isShowSectors = !this.isShowSectors;
    }

    public getBlockedByPoleTextValue(): string {
        const selectedOption = this.blockedByPoleControl.findSelectedOption();
        return selectedOption?.editType ? this.blockedByPoleControl.value + '\u00B0' : selectedOption.text;
    }

    public getDeviceAzimuthTextValue(): string {
        const selectedOption = this.deviceAzimuthControl.findSelectedOption();
        return selectedOption?.editType ? this.deviceAzimuthControl.value + '\u00B0' : selectedOption.text;
    }

    private createCommand() {
        this.modifyInfoCommand = this.historyService.command(GtthModifyUnitInfoCommand).create(this.entityId);
        this.modifyInfoCommand.setName(this.station.getName());

        this.hasFreezeLinks = this.station.hasFreezeLinks();

        this.isEditingEnabled =
            this.historyService.isEditModeEnabled &&
            this.historyService.selectedStepNumber === GtthStepGroupEnum.LOCATIONS_AND_LOSES &&
            !this.hasFreezeLinks;

        this.isPoPControl.setValue(this.station.isPop() ? IsPopEnum.IS_POP : IsPopEnum.NO_POP);
        this.polarityControl.setValue(this.station.getBuDevicePolarity());

        this.initLayersControl();
        this.initOrUpdateMhTypeAndProductControls();
        this.initOrUpdateBlockedByPoleControl();
        this.initOrUpdateDeviceAzimuthControl();

        this.allowLosStations = this.historyService.logicalTopology.getAllowStationsForLoses(this.entityId);
        this.losToVerify = this.historyService.logicalTopology.getExistLosForStation(this.entityId);
        this.unitSectorsCount = this.historyService.logicalTopology.sectorsService.getStationSectorsCount(this.entityId);
        this.isShowSectors = this.historyService.logicalTopology.isHasNodeNamesSectorsToShow(this.modifyInfoCommand.getName());

        this.eventManager.broadcast({
            name: 'updateConfigShow',
            entityId: this.station.getId(),
            modifyCommand: this.modifyInfoCommand
        });
    }

    private initLayersControl(): void {
        if (!this.historyService.logicalTopology.layersModeEnabled) {
            return;
        }

        const layersOptions = this.historyService.logicalTopology.layers.map((layer: GtthLayerDtoModel) => {
            return { key: layer.layerIndex, value: layer.getFullName() } as GtthKeyValueDTO;
        });
        const selected = layersOptions.find((layer) => layer.key === this.station.getLayerIndex());
        this.layersControl = new SuiTypeSelect<GtthKeyValueDTO>(selected, layersOptions, 'value');
        this.layersControl.isAlignedStyles = true;
    }

    private initOrUpdateMhTypeAndProductControls(): void {
        this.mhTypeControl.options = [NodeTypeEnum.BEST_EFFORT];

        this.projectHasBuProducts = this.isProjectHasAnyBuDeviceOnLayer(this.station.getLayerIndex());

        if (this.projectHasBuProducts) {
            const mhTgProducts = this.getProductOptions();

            if (mhTgProducts.length > 1) {
                this.mhTypeControl.options.push(NodeTypeEnum.BU_MUST);
                const selectedProduct = mhTgProducts.find((value) => value.id === this.station.getPredefinedBuProductId());
                this.mhTgProductControl = new SuiTypeSelect<EngineProductDTO>(selectedProduct || mhTgProducts[0], mhTgProducts, 'name');
                this.mhTgProductControl.isAlignedStyles = true;
                this.mhTgProductControl.dropdownConfig.panelClass = 'entities-info-select-dropdown';
            }

            if (!this.station.isPop()) {
                this.mhTypeControl.options.push(NodeTypeEnum.OPTIONAL_BU);
                this.mhTypeControl.options.push(NodeTypeEnum.NO_BU);
            }
        }

        if (this.mhTypeControl.options.includes(this.station.getMhType())) {
            this.mhTypeControl.setValue(this.station.getMhType());
        } else {
            const newMhType = this.mhTypeControl.options[0];
            this.setMhType(newMhType);
        }
    }

    private initOrUpdateBlockedByPoleControl(): void {
        if (!this.blockedByPoleControl) {
            this.blockedByPoleControl = new SuiTypeEditableSelect<number>(this.station.getBlockedByPoleAngle(), [
                new SuiEditableSelectOption<number>('No', GtthNodeFeatureDtoModel.POLE_BLOCKED_NO),
                new SuiEditableSelectOption<number>('Auto', GtthNodeFeatureDtoModel.DEFAULT_POLE_BLOCKED_ANGLE),
                new SuiEditableSelectOption<number>('Value', this.station.getBlockedByPoleAngle(), new SuiTypeNumber(20, 20, 20, 70))
            ]);
        }

        const defaultBlockByPole = this.historyService.logicalTopology.autodesignP2mpPerLayer[this.station.getLayerIndex()].pole_block;
        this.blockedByPoleControl.options[1].text = 'Auto (' + defaultBlockByPole + '\u00B0)';
    }

    private initOrUpdateDeviceAzimuthControl(): void {
        if (!this.deviceAzimuthControl) {
            this.deviceAzimuthControl = new SuiTypeEditableSelect<number>(this.station.getBuDeviceAzimuth(), [
                new SuiEditableSelectOption<number>('Global', GtthNodeFeatureDtoModel.DEFAULT_BU_DEVICE_AZIMUTH),
                new SuiEditableSelectOption<number>('Value', this.station.getBuDeviceAzimuth(), new SuiTypeNumber(0, 0, 0, 359))
            ]);
        }
        const defaultDeviceAzimuth =
            this.historyService.logicalTopology.autodesignP2mpPerLayer[this.station.getLayerIndex()].buDeviceAzimuth;
        this.deviceAzimuthControl.options[0].text = 'Global (' + defaultDeviceAzimuth + '\u00B0)';
    }

    private getProductOptions(): EngineProductDTO[] {
        const layerBuProductIds = this.historyService.logicalTopology.selectedProducts[this.station.getLayerIndex()]
            .filter((selectedProduct) => this.projectProductsService.isMhTgBuDevice(selectedProduct.usedProd))
            .map((value) => value.usedProd);

        const buProductsSet = new Set<number>(layerBuProductIds);

        return [this.productNotDefinedOption, ...Array.from(buProductsSet).map((id) => this.projectProductsService.getBaseRadioById(id))];
    }

    private isProjectHasAnyBuDeviceOnLayer(layerIndex: number): boolean {
        return this.historyService.logicalTopology.selectedProducts[layerIndex].some((product) => {
            return this.projectProductsService.isDeviceBU(product.usedProd);
        });
    }

    private setPredefinedBuProductIdOrSelectFirst(): void {
        const selectedMhTgProduct = this.mhTgProductControl.options.find((option) => option.id === this.station.getPredefinedBuProductId());
        this.setPredefinedBuProductId(selectedMhTgProduct || this.mhTgProductControl.options[0]);
    }
}
